/**
 * A figure's clock.
 *
 * Several figures are *staged*: pools resolve, then the eligibility gate rules,
 * then the weights settle, then a decision is written. Each stage needs to know
 * which step it is on, and the steps must not drift apart — five independent
 * `setTimeout`s in five components is how a figure ends up showing a decision
 * before the candidates it chose from.
 *
 * Under reduced motion this returns the **last** step immediately. That is the
 * resting state, and the resting state is the true and final one — the rule the
 * whole kit is built on. There is no timer at all in that mode: nothing to
 * cancel, nothing to leak, and no first animated frame to catch.
 */
import { useEffect, useState } from 'react'

import { useInViewOnce } from './useInViewOnce'
import { useReducedMotionSafe } from './motion'

export function useSequence(
  /** Milliseconds to hold each step. One entry per step. */
  steps: readonly number[],
  { loop = false }: { loop?: boolean } = {},
): [React.RefObject<HTMLDivElement | null>, number] {
  const reduced = useReducedMotionSafe()
  const [ref, seen] = useInViewOnce<HTMLDivElement>()
  const [step, setStep] = useState(reduced ? steps.length - 1 : 0)

  /*
   * The dependency is the schedule's *contents*, not the array's identity.
   *
   * This is not a micro-optimisation, it is a correctness fix, and it cost an
   * afternoon: a caller writing `useSequence([900, 1100])` inline hands a new
   * array on every render. With the array itself as the dependency the effect
   * re-runs after each tick, its cleanup kills the pending timer and its body
   * resets the step to 0 — so the figure re-renders forever and never gets past
   * its first step. The symptom is not a crash or a warning; it is a figure that
   * looks empty and a fan that spins up. Serialising makes a caller's ordinary
   * inline literal safe, which is the only way this stays fixed.
   */
  const schedule = steps.join(',')

  useEffect(() => {
    if (reduced || !seen) return
    const plan = schedule.split(',').map(Number)
    let timer: ReturnType<typeof setTimeout>
    let current = 0
    setStep(0)
    const advance = () => {
      const next = current + 1
      if (next >= plan.length) {
        if (!loop) return
        current = 0
      } else {
        current = next
      }
      setStep(current)
      timer = setTimeout(advance, plan[current])
    }
    timer = setTimeout(advance, plan[0])
    return () => clearTimeout(timer)
  }, [loop, reduced, seen, schedule])

  return [ref, reduced ? steps.length - 1 : step]
}
